import React, { useState, useEffect } from 'react';
import { FiPlay, FiPause, FiVolume2, FiVolumeX } from 'react-icons/fi';

const CustomVideoControls = ({ player }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (!player) return;

    const interval = setInterval(() => {
      try {
        if (player.getCurrentTime) {
          setCurrentTime(player.getCurrentTime());
        }
        if (player.getDuration) {
          setDuration(player.getDuration());
        }
        if (player.getPlayerState) {
          const state = player.getPlayerState();
          setIsPlaying(state === 1); // 1 = playing
        }
      } catch (error) {
        console.log('Player not ready yet');
      }
    }, 100);

    return () => clearInterval(interval);
  }, [player]);

  const togglePlayPause = () => {
    if (!player) return;
    
    try {
      if (isPlaying) {
        player.pauseVideo();
        setIsPlaying(false);
      } else {
        player.playVideo();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Error toggling play/pause:', error);
    }
  };

  const handleSeek = (e) => {
    if (!player || !duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const time = percentage * duration;
    
    try {
      player.seekTo(time, true);
    } catch (error) {
      console.error('Error seeking:', error);
    }
  };

  const handleVolumeChange = (e) => {
    if (!player) return;
    
    const newVolume = parseInt(e.target.value);
    setVolume(newVolume);
    
    try {
      player.setVolume(newVolume);
      
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    } catch (error) {
      console.error('Error changing volume:', error);
    }
  };

  const toggleMute = () => {
    if (!player) return;
    
    try {
      if (isMuted) {
        player.unMute();
        player.setVolume(volume || 50);
        setIsMuted(false);
      } else {
        player.mute();
        setIsMuted(true);
      }
    } catch (error) {
      console.error('Error toggling mute:', error);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!player) return null;

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-3 md:p-4 z-30">
      {/* Progress Bar */}
      <div 
        className="w-full h-1 bg-gray-600 rounded-full mb-3 cursor-pointer hover:h-2 transition-all group"
        onClick={handleSeek}
      >
        <div 
          className="h-full bg-blue-500 rounded-full relative"
          style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
        >
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between text-white">
        <div className="flex items-center gap-2 md:gap-3">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlayPause}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? (
              <FiPause className="w-4 h-4 md:w-5 md:h-5" />
            ) : (
              <FiPlay className="w-4 h-4 md:w-5 md:h-5" />
            )}
          </button>

          {/* Time Display */}
          <span className="text-xs md:text-sm font-medium">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Volume Control */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggleMute}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              aria-label={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <FiVolumeX className="w-4 h-4 md:w-5 md:h-5" />
              ) : (
                <FiVolume2 className="w-4 h-4 md:w-5 md:h-5" />
              )}
            </button>
            <input
              type="range"
              min="0"
              max="100"
              value={isMuted ? 0 : volume}
              onChange={handleVolumeChange}
              className="w-16 md:w-20 h-1 bg-gray-600 rounded-full hidden sm:block volume-slider"
              aria-label="Volume"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoControls;

